import classNames from 'classnames';
import { differenceInMonths, format as formatDate, isAfter } from 'date-fns';
import { useMemo } from 'react';
import { Badge, Heading } from 'yoso';
import { Currency } from '~/components/Currency';
import { T } from '~/components/I18n/Translation';
import {
  LandHistory,
  useLandHistoryLastVisitedDate,
} from '~/components/Land/History/LandHistory.hooks';
import { landMeta, zoneImageMap } from '~/components/Land/Land.meta';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import dlStyles from '~/components/List/Description/DescriptionList.module.scss';
import { sharedDateTimer } from '~/components/Util';
import { format } from '~/components/Util/number';
import styles from './LandHistoryItem.module.scss';

export type LandHistoryItemProps = {
  className?: string;
} & LandHistory;

export const LandHistoryItem = ({
  className,
  landType,
  zoneNumber,
  snapshotLup,
  snapshotNftLevel,
  snapshotNftCount,
  category,
  rewardAmountInFnsa = 0,
  rewardAmountInUsd = 0,
  updatedAt,
  participationAt,
}: LandHistoryItemProps) => {
  const ZoneImage = zoneImageMap[`level${snapshotNftLevel}_zone${zoneNumber}`] || 'img';

  const { value: lastVisit } = useLandHistoryLastVisitedDate();
  const isNew = useMemo(() => {
    if (differenceInMonths(sharedDateTimer.now, updatedAt) > 0) return false;
    return isAfter(updatedAt, lastVisit);
  }, [lastVisit, updatedAt]);

  return (
    <div className={classNames(styles.root, styles[category], className)}>
      <div className={styles.aside}>
        <ZoneImage className={styles.image} width={40} height={40} />
      </div>
      <div className={styles.body}>
        <Heading className={styles.heading} level={5}>
          {landMeta[landType].title} - <T name="citizen_land_zone" /> {zoneNumber}
          {category === 'WINS' && (
            <Badge className={classNames(styles.badge, styles.winBadge)}>Win</Badge>
          )}
          {isNew && <Badge className={classNames(styles.badge, styles.newBadge)}>New</Badge>}
        </Heading>
        <dl className={classNames(dlStyles.inline, styles.list)}>
          <dd>
            <DescriptionList className={styles.sublist} layout="inline">
              <dt>LUP</dt>
              <dd>
                <strong>{format(snapshotLup)}</strong>
              </dd>
              <dt>LV{snapshotNftLevel}</dt>
              <dd>
                <strong>{format(snapshotNftCount)}</strong>
              </dd>
            </DescriptionList>
          </dd>
          <dd className={dlStyles.divider}>
            <p>{formatDate(participationAt, 'PP p')}</p>
          </dd>
        </dl>
      </div>
      <div className={styles.footer}>
        <p className={styles.status}>
          {(() => {
            switch (category) {
              case 'WINS':
                return (
                  <span>
                    <T
                      name="citizen_land_myhistory_status3"
                      options={[
                        <Currency
                          key={1}
                          currency="FNSA"
                          value={rewardAmountInFnsa}
                          maximumFractionDigits={6}
                          minimumFractionDigits={0}
                        />,
                      ]}
                    />
                    <small>
                      ≈
                      <Currency
                        currency="USD"
                        value={rewardAmountInUsd}
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                      />
                    </small>
                  </span>
                );
              case 'NOT_WINS':
                return <T name="citizen_land_myhistory_status2" />;
              case 'REFUNDED':
                return <T name="citizen_land_myhistory_status4" />;
              default:
                return <T name="citizen_land_myhistory_status1" />;
            }
          })()}
        </p>
      </div>
    </div>
  );
};
